import { useState, FormEvent } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'

interface ChildInfo {
  id: number
  displayName: string
  username: string
}

interface Props {
  children: ChildInfo[]
  onComplete: () => void
  onSkip: () => void
}

export default function InviteStep({ children, onComplete, onSkip }: Props) {
  const [email, setEmail] = useState('')
  const [selectedChildId, setSelectedChildId] = useState(children[0]?.id || 0)
  const [role, setRole] = useState<'VIEWER' | 'MANAGER'>('VIEWER')
  const [invites, setInvites] = useState<{ email: string; childName: string }[]>([])
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await apiFetch('/onboarding/invite', {
        method: 'POST',
        body: JSON.stringify({ childId: selectedChildId, email, role }),
      })
      const childName = children.find(c => c.id === selectedChildId)?.displayName || ''
      setInvites(prev => [...prev, { email, childName }])
      setEmail('')
    } catch (err: any) {
      setError(err.message || 'Failed to send invite.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="w-full max-w-sm mx-auto space-y-6">
      <MagnumSays>
        Want to invite a co-parent or family member? They'll be able to see progress and cheer your reader on!
      </MagnumSays>

      {invites.length > 0 && (
        <div className="space-y-2">
          {invites.map((inv, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200 text-sm">
              <span className="text-green-600 font-medium">Invited</span>
              <span className="text-foreground">{inv.email}</span>
              <span className="text-muted-foreground">for {inv.childName}</span>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleInvite} className="space-y-3">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        <div>
          <label htmlFor="ob-inviteEmail" className="block text-sm font-medium mb-1">Email Address</label>
          <input
            id="ob-inviteEmail"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="partner@example.com"
          />
        </div>

        {children.length > 1 && (
          <div>
            <label htmlFor="ob-inviteChild" className="block text-sm font-medium mb-1">For which child?</label>
            <select
              id="ob-inviteChild"
              value={selectedChildId}
              onChange={(e) => setSelectedChildId(Number(e.target.value))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {children.map(c => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">Access Level</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setRole('VIEWER')}
              className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
                role === 'VIEWER' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}
            >
              Viewer
            </button>
            <button
              type="button"
              onClick={() => setRole('MANAGER')}
              className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
                role === 'MANAGER' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}
            >
              Manager
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {role === 'VIEWER' ? 'Can view progress and activity' : 'Can view progress, edit goals, and change passwords'}
          </p>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
        >
          {submitting ? 'Inviting...' : 'Send Invite'}
        </button>
      </form>

      <div className="flex gap-2">
        {invites.length > 0 && (
          <button
            onClick={onComplete}
            className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition"
          >
            Continue
          </button>
        )}
        <button
          onClick={onSkip}
          className={`${invites.length > 0 ? 'flex-1' : 'w-full'} py-2.5 border border-border rounded-lg font-medium text-muted-foreground hover:text-foreground transition`}
        >
          {invites.length > 0 ? 'Done inviting' : 'Skip for now'}
        </button>
      </div>
      <p className="text-xs text-center text-muted-foreground">You can always invite people later in Settings.</p>
    </div>
  )
}
